import { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { getOptions, pioReferenceRegex } from "..";
import {
  getAmcMasters,
  swpOptions,
  getUmrnList,
  checkoutNewPurchase,
  sipNewPurchase,
  existingFolioSIPwithOrwithoutPayment,
  getLumpsumPaymodes,
  UmrnOptions,
} from "../../../../../store/actions";
import {
  CartItems,
  CartItemsMap,
  Option,
  Options,
  PAYMENT_MODE,
} from "../../../../../store/types";
import {
  convertDate,
  convertDateToIndian,
  getSipAmountAndDates,
  handleError,
  isNotAotm,
  isValidMandateFilter,
} from "../../../../../utils/cart";
import { useAppSelector } from "../../../../../store/hooks";
import * as Yup from "yup";
import { RootState } from "../../../../../store/reducers";
import { getFrequencyName } from "../../../transactions/tranUtils";
import {
  CalcSIPEnddt,
  checkAotmStatus,
  getInvestorCategory,
  updateIsipCart,
} from "../../../../../store/actions/cart";
import moment from "moment";
import { validateMandateEndDate } from "./useSipWithoutPayment";
interface CartLocation {
  requestBody: {
    selectedCartItems: Array<string>;
    pan: string;
    arnCode: string;
    cartId: string;
  };
}

interface SingleLocation {
  requestBody: CartItemsMap["SIN"];
}

export interface Values {
  umrn?: string;
  registration: "AOTM" | "KOTM" | "ISIP" | "";
  paymentMode: "UPI" | "KOTM" | "NETBANKING" | "PIO";
  utrno?: string;
}

export interface OnChange extends ChangeEvent<{ name?: string; value: any }> {}

export type HandleChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: OnChange) => any;

export const validationSchema = (isPioEnabled: boolean) =>
  Yup.object().shape({
    registration: Yup.string().required("Required"),
    umrn: Yup.string().when("registration", {
      is: (value: string) => value && value.includes("OTM"),
      then: (schema) => schema.required("Please choose a mandate"),
      otherwise: (schema) => schema.notRequired(),
    }),
    paymentMode: Yup.string().required("Payment mode  is required"),
    utrno: Yup.string().test(
      "utrNumber",
      "Enter a valid PIO Reference Number",
      function (value) {
        if (!isPioEnabled) return true;
        const { paymentMode } = this.parent;
        if (paymentMode !== "PIO") return true;
        return pioReferenceRegex.test(value);
      }
    ),
  });

const NEW_MANDATE_OPTION = [
  {
    label: "+ Create New Mandate",
    value: "createMandate",
    startDate: "",
    endDate: "",
    mandateLimit: 0,
    bankAccountNumber: "",
    bankName: "",
  },
];

type UpdatedRequest = SingleLocation["requestBody"] & {
  oldStartDate: string;
  oldEndDate: string;
};

export function useSipCart() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { state } = useLocation<CartLocation>();

  const cartItems = useAppSelector(cartItemsSelector);

  const [isNri, setIsNri] = useState(cartItems[0].payload.invCategory === "20");

  const [amcMasters, setAmcMasters] = useState<Options>([]);
  const [paymodes, setPaymodes] = useState<Options>([]);
  const [umrnOptions, setUmrnOptions] = useState<UmrnOptions>([]);
  const [registrationModes, setRegistrationModes] = useState<Options>([]);

  const [oldCart, setOldCart] = useState<CartItems<"SIN">>([]);

  const initialValues: Values = {
    registration: cartItems[0].payload.modeOfRegistration,
    paymentMode: "NETBANKING",
    umrn: "",
  };

  const { requestBody } = state;
  const { selectedCartItems: selectedCartItemIds } = requestBody;

  const selectedCartItems = useMemo(function () {
    return cartItems.filter((cartItem) =>
      selectedCartItemIds.includes(cartItem.cartItemId)
    );
  }, []);

  const [fund, pan, folio = "0"] = [
    cartItems[0].fund,
    cartItems[0].pan,
    cartItems[0].payload.folio,
  ];

  const folios = selectedCartItems.map((i) => i.payload.folio);
  const isAdditionalPurchase = !cartItems[0].payload.newPurchaseSipType;

  const isSameFolio =
    new Set(folios.map(Number)).size === 1 && isAdditionalPurchase;

  const isIsip = cartItems[0].payload.modeOfRegistration === "ISIP";

  const fetchPaymentMasters = async function (isNri: boolean) {
    try {
      const requestBody = {
        Fund: cartItems[0].fund,
        astscheme: cartItems[0].pan,
        opt: PAYMENT_MODE,
        acno: folio,
      };

      const response = await swpOptions(requestBody)(dispatch);
      const isPanBasedAotm = await checkAotmStatus(fund)(dispatch);

      const registrationModeOptions = response?.Dtinformation.map(getOptions)
        .filter((i: Option) =>
          isSameFolio || isPanBasedAotm ? true : i.value !== "AOTM"
        )
        .filter((i: Option) => {
          if (isIsip) {
            return true;
          }
          if (isNri) {
            return true;
          }
          if (isPanBasedAotm || isSameFolio) {
            return i.value !== "ISIP";
          }
          return i.value !== "AOTM" && i.value !== "ISIP";
        })
        .filter((o: Options[number]) => {
          if (fund !== "129") return true;
          return isNotAotm(o);
        });

      setRegistrationModes(registrationModeOptions);

      const lumpusumPaymodes = await getLumpsumPaymodes(
        fund,
        pan,
        "SIN",
        isSameFolio ? folio : "0"
      )(dispatch);

      setPaymodes(lumpusumPaymodes);
    } catch (error) {
      handleError(error);
    }
  };

  const handleRegistrationChange: HandleChange = function (values, setValues) {
    return async function (e) {
      try {
        const registrationMode = e.target.value;
        const updatedValues = {
          ...values,
          registration: registrationMode,
          umrn: "",
        };
        const isConfirmed = window.confirm(
          `Changing mode of regsistration to ${registrationMode} may update the startdate and enddates of your SIP transaction/s`
        );
        if (!isConfirmed) return;
        if (selectedCartItems.length !== cartItems.length) {
          throw new Error(
            "Please select all the cart items to change mode of registration"
          );
        }
        if (registrationMode !== "ISIP") {
          await fetchUmrnList(registrationMode,cartItems[0].payload);
        }
        setValues(updatedValues);
        setOldCart(cartItems);
        await updateCart(registrationMode);
        return;
      } catch (error) {
        handleError(error);
      }
    };
  };

  const getLumpsumMax = function () {
    const amounts = selectedCartItems
      .map((item) => item.payload.amount)
      .map(Number);
    return Math.max(...amounts);
  };

  const handleUmrnChange: HandleChange = function (values, setValues) {
    return async function (e) {
      const umrn = e.target.value;

      if (umrn === "createMandate") {
        setValues((prev) => ({
          ...prev,
          umrn,
          registration: "KOTM",
        }));
        return;
      }
      setValues((prev) => ({
        ...prev,
        umrn,
      }));
    };
  };

  const updateCart = async function (mode: Values["registration"]) {
    const dates = cartItems.map(({ cartItemId }) => ({
      cartItemId,
      mode: mode,
    }));
    return await updateIsipCart(dates)(dispatch);
  };

  const handleSubmit = async function (values: Values) {
    try {
      const isNewMandate =
        values.umrn === "createMandate" && values.registration === "KOTM";
      const isIsip = values.registration === "ISIP";

      const selectedMandateDetails = umrnOptions.find(
        (option) => option.value === values.umrn
      );
      await updateCart(values.registration);
      const cartPayload = {
        ...requestBody,
        createMandate: isNewMandate,
        modeOfRegistration: values.registration,
        umrn: isIsip || isNewMandate ? "" : values.umrn,
        selectedMandates: selectedMandateDetails,
        euin: cartItems[0].payload.euin,
        subBroker: cartItems[0].payload.subBroker,
        subArn: cartItems[0].payload.subArn,
        paymentDetails: {
          paymentMode: values.paymentMode,
          pioTransaction: values.paymentMode === "PIO",
          utrno: values.paymentMode === "PIO" ? values.utrno : "",
        },
      };

      const response = await checkoutNewPurchase(cartPayload)(dispatch);
      if (!response) return;
      const { batchId } = response;
      const message = `Transaction submitted successfully with the batch reference number  ${batchId}`;
      alert(message);
      history.replace("/investors");
    } catch (error) {
      handleError(error);
    }
  };

  const fetchUmrnList = async function (mode: "AOTM" | "KOTM",payload:any) {
    try {
      const isPanBasedAotm = await checkAotmStatus(fund)(dispatch);
      const folio =
        mode === "KOTM" || isPanBasedAotm ? 0 : isSameFolio ? folios[0] : 0;
      const response = await getUmrnList({
        fund: fund,
        pan: pan,
        folio: folio,
        modeOfRegistration: mode,
      })(dispatch);

      const validMandates = response.filter((args) =>
        isValidMandateFilter(args, getSipAmountAndDates(selectedCartItems),payload)
      );

      if (validMandates.length === 0 && isNri) {
        throw new Error(
          "No mandates with sufficient amount limit/duration available for the selected registration mode, Please select different mode of registration"
        );
      }

      if (mode === "AOTM" && validMandates.length === 0) {
        throw new Error(
          "No mandates with sufficient amount limit/duration available for the selected registration mode, Please select  different mode of registration"
        );
      }
      if (mode === "KOTM" && !isNri) {
        setUmrnOptions(NEW_MANDATE_OPTION.concat(validMandates));
        return;
      }
      setUmrnOptions(validMandates);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    (async function () {
      if (amcMasters.length) return;
      const masters = await getAmcMasters()(dispatch);
      setAmcMasters(masters);
    })();
    fetchPaymentMasters(isNri);
  }, [isNri]);

  useEffect(() => {
    if (!isAdditionalPurchase) return;
    (async function () {
      const response = await getInvestorCategory(
        fund,
        String(folios[0])
      )(dispatch);

      const { invcat } = response;
      setIsNri(invcat !== "11");
    })();
  }, []);

  useEffect(() => {
    try {
      const modeOfRegistration = cartItems[0].payload.modeOfRegistration;
      const payload = cartItems[0].payload
      const isIsip = modeOfRegistration === "ISIP";

      if (isIsip) return;
      if (modeOfRegistration === "") return;

      fetchUmrnList(modeOfRegistration,payload);
    } catch (error) {
      handleError(error);
    }
  }, []);

  return {
    options: {
      umrnOptions,
      registrationModes,
      paymodes,
    },
    changleHandler: {
      handleRegistrationChange,
      handleUmrnChange,
    },
    handleSubmit,
    amcName: amcMasters.find(({ value }) => value === fund)?.label,
    initialValues,
    getLumpsumMax,
    investorName: selectedCartItems[0].investorName,
    pan: pan,
    selectedCartItems,
    oldCart,
  };
}

export function useSipSingle() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { state } = useLocation<SingleLocation>();

  const { requestBody } = state;

  const { modeOfRegistration } = requestBody;

  const [isNri, setIsNri] = useState(requestBody.invCategory === "20");

  const [amcMasters, setAmcMasters] = useState<Options>([]);
  const [paymodes, setPaymodes] = useState<Options>([]);
  const [umrnOptions, setUmrnOptions] = useState<UmrnOptions>([]);
  const [registrationModes, setRegistrationModes] = useState<Options>([]);

  const [updatedRequest, setUpdatedRequest] = useState<UpdatedRequest | null>(
    null
  );

  const initialValues: Values = {
    registration: modeOfRegistration,
    paymentMode: "NETBANKING",
    umrn: "",
  };

  const [fund, pan, folio = "0"] = [
    requestBody.fund,
    requestBody.pan,
    requestBody.folio,
  ];

  const isAdditionalPurchase = !requestBody.newPurchaseSipType;

  const isIsip = modeOfRegistration === "ISIP";
  const isOtm = modeOfRegistration.includes("OTM");

  const fetchPaymentMasters = async function (isNri: boolean) {
    try {
      const requestBody = {
        Fund: fund,
        astscheme: pan,
        opt: PAYMENT_MODE,
        acno: folio,
      };
      const response = await swpOptions(requestBody)(dispatch);
      const isPanBasedAotm = await checkAotmStatus(fund)(dispatch);

      const registrationModeOptions = response?.Dtinformation.map(getOptions)
        .filter((i: Option) => {
          if (isIsip) {
            return true;
          }
          if (isNri) {
            return true;
          }
          if (isPanBasedAotm || Boolean(folio)) {
            return i.value !== "ISIP";
          }
          return i.value !== "ISIP" && i.value !== "AOTM";
        })
        .filter((i: Option) => {
          if (fund !== "129") return true;
          return i.value !== "AOTM";
        });

      setRegistrationModes(registrationModeOptions);

      const lumpusumPaymodes = (
        await getLumpsumPaymodes(fund, pan, "SIN", folio)(dispatch)
      ).filter((option) =>
        isPanBasedAotm || folio ? true : option.value !== "AOTM"
      );

      setPaymodes(lumpusumPaymodes);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchDates = async function (mode: Values["registration"]) {
    const payload = {
      fund,
      sch: requestBody.scheme,
      StartDate: requestBody.sipDay,
      Installments: requestBody.noOfInstallments,
      Frequency: getFrequencyName(requestBody.sipFrequency),
      modeOfRegistration: mode,
      trtype: "SIP",
    };
    const response = await CalcSIPEnddt(payload)(dispatch);
    const { DtData } = response;
    const [Dates] = DtData;
    const { SIP_StartDate, SIP_EndDate } = Dates;

    if (requestBody.perpetual && modeOfRegistration.includes("OTM")) {
      const today = new Date();
      const todayDate = today.getDate();
      const todayYear = today.getFullYear();
      const endDate = new Date(new Date().setDate(todayDate - 1));
      const sipEndDate = new Date(endDate.setFullYear(todayYear + 40));

      const updatedPayload = {
        ...requestBody,
        sipStartDate: convertDate(SIP_StartDate),
        sipEndDate: moment(sipEndDate).format("DD/MM/YYYY"),
        oldStartDate: requestBody.sipStartDate,
        oldEndDate: requestBody.sipEndDate,
      };
      return {
        ...requestBody,
        ...updatedPayload,
      };
    }
    if (requestBody.perpetual && modeOfRegistration === "ISIP") {
      const endDate = new Date(2099, 11, 31);

      const updatedPayload = {
        ...requestBody,
        sipStartDate: convertDate(SIP_StartDate),
        sipEndDate: convertDate(convertDateToIndian(endDate)),
        oldStartDate: requestBody.sipStartDate,
        oldEndDate: requestBody.sipEndDate,
      };

      return {
        ...requestBody,
        ...updatedPayload,
      };
    }

    return {
      ...requestBody,
      oldEndDate: requestBody.sipStartDate,
      oldStartDate: requestBody.sipEndDate,
      sipStartDate: convertDate(SIP_StartDate),
      sipEndDate: convertDate(SIP_EndDate),
    };
  };

  const calculateUpdatedDates = async function (mode: Values["registration"]) {
    try {
      const response = await fetchDates(mode);
      setUpdatedRequest(response);
    } catch (error) {
      handleError(error);
    }
  };

  const handleRegistrationChange: HandleChange = function (values, setValues) {
    return async function (e) {
      try {
        const registrationMode = e.target.value;
        const updatedValues = {
          ...values,
          registration: registrationMode,
          umrn: "",
        };
        if (registrationMode.includes("OTM") && isOtm) {
          await fetchUmrnList(registrationMode,requestBody);
          setValues(updatedValues);
          setUpdatedRequest(null);
          return;
        }

        if (registrationMode === "ISIP" && isIsip) {
          await fetchUmrnList(registrationMode,requestBody);
          setValues(updatedValues);
          setUpdatedRequest(null);
          return;
        }

        const isConfirmed = window.confirm(
          "Changing mode of regsistration to ISIP may update the startdate and enddates of your SIP transaction/s"
        );

        if (!isConfirmed) return;
        await calculateUpdatedDates(registrationMode);
        setValues(updatedValues);
        return;
      } catch (error) {
        handleError(error);
      }
    };
  };

  const handleUmrnChange: HandleChange = function (values, setValues) {
    return async function (e) {
      const umrn = e.target.value;

      if (umrn === "createMandate") {
        setValues((prev) => ({
          ...prev,
          umrn,
          registration: "KOTM",
        }));
        return;
      }
      setValues((prev) => ({
        ...prev,
        umrn,
      }));
    };
  };

  const handleNewPurchase = async function (requestBody: any) {
    const response = await sipNewPurchase(requestBody)(dispatch);
    const { ihno } = response;
    if (!response) return;
    const message = `Transaction initiated successfully with the reference number ${ihno}`;
    // alert(message);
    history.replace("/investors");
  };

  const handleSubmit = async function (values: Values) {
    try {
      const isNewMandate =
        values.umrn === "createMandate" && values.registration === "KOTM";

      const isIsip = values.registration === "ISIP";

      const selectedMandateDetails = umrnOptions.find(
        (option) => option.value === values.umrn
      );

      const payload = {
        ...requestBody,
        ...updatedRequest,
        createMandate: isNewMandate,
        modeOfRegistration: values.registration,
        umrn: isIsip || isNewMandate ? "" : values.umrn,
        selectedMandates: selectedMandateDetails,
        euin: requestBody.euin,
        subBroker: requestBody.subBroker,
        subArn: requestBody.subArn,
        paymentDetails: {
          paymentMode: values.paymentMode,
          pioTransaction: values.paymentMode === "PIO",
          utrno: values.paymentMode === "PIO" ? values.utrno : "",
        },
        paymentMode: values.paymentMode,
        pioTransaction: values.paymentMode === "PIO",
        utrno: values.paymentMode === "PIO" ? values.utrno : "",
      };


      if(requestBody.sipAutoRenewal && (requestBody.modeOfRegistration==="KOTM" || requestBody.modeOfRegistration==="AOTM")){
        validateMandateEndDate(requestBody,selectedMandateDetails)
      }
      if (requestBody.newPurchaseSipType === "SIPN") {
        await handleNewPurchase(payload);
        return;
      }

      const action = existingFolioSIPwithOrwithoutPayment(payload);
      const response = await action(dispatch);

      if (!response) return;
      history.replace("/investors");
    } catch (error) {
      handleError(error);
    }
  };

  const fetchUmrnList = async function (mode: "AOTM" | "KOTM",requestBody:any) {
    try {
      const isPanBasedAotm = await checkAotmStatus(fund)(dispatch);
      const response = await getUmrnList({
        fund: fund,
        pan: pan,
        folio: mode === "AOTM" && !isPanBasedAotm ? requestBody.folio : 0,
        modeOfRegistration: mode,
      })(dispatch);

      const validMandates = response.filter((args) =>
        isValidMandateFilter(args, {
          earliestDate: new Date(requestBody.sipStartDate),
          latestDate: new Date(requestBody.sipEndDate),
          greatestAmount: Number(requestBody.amount),
        },requestBody)
      );
      if (validMandates.length === 0 && isNri) {
        throw new Error(
          "No mandates with sufficient amount limit/duration available for the selected registration mode, Please select different mode of registration"
        );
      }

      if (mode === "AOTM" && validMandates.length === 0) {
        throw new Error(
          "No mandates with sufficient amount limit/duration available for the selected registration mode, Please select  different mode of registration"
        );
      }
      if (mode === "KOTM" && !isNri) {
        setUmrnOptions(NEW_MANDATE_OPTION.concat(validMandates));
        return;
      }
      setUmrnOptions(validMandates);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    (async function () {
      if (amcMasters.length) return;
      const masters = await getAmcMasters()(dispatch);
      setAmcMasters(masters);
    })();
    fetchPaymentMasters(isNri);
  }, [isNri]);

  useEffect(() => {
    if (!isAdditionalPurchase) {
      return;
    }
    (async function () {
      const response = await getInvestorCategory(
        fund,
        String(requestBody.folio)
      )(dispatch);
      const { invcat } = response;
      setIsNri(invcat !== "11");
    })();
  }, []);

  useEffect(() => {
    const modeOfRegistration = requestBody.modeOfRegistration;
    const isIsip = modeOfRegistration === "ISIP";

    if (isIsip) return;
    if (modeOfRegistration === "") return;

    fetchUmrnList(modeOfRegistration,requestBody);
  }, []);

  return {
    options: {
      umrnOptions,
      registrationModes,
      paymodes,
    },
    changleHandler: {
      handleRegistrationChange,
      handleUmrnChange,
    },
    handleSubmit,
    amcName: amcMasters.find(({ value }) => value === fund)?.label,
    initialValues,
    pan: pan,
    requestBody,
    updatedRequest,
    investorName: requestBody.investorName,
  };
}

function cartItemsSelector({ cartReducer }: RootState): CartItems<"SIN"> {
  const { cartItems } = cartReducer;
  // @ts-ignore
  return cartItems;
}
